import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Globe, Lock, User, FolderOpen } from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { wordpressService } from '../../services/wordpressService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';

export default function AddSite() {
  const navigate = useNavigate();
  const { activeProfile } = useLanguageProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    siteName: '',
    siteUrl: '',
    wpUsername: '',
    wpAppPassword: '',
    defaultCategory: ''
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await wordpressService?.createSite({ ...formData, profileId: activeProfile?.id || null });
      navigate('/wp/sites');
    } catch (err) {
      setError(err?.message || 'Failed to add site');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-8">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-lg shadow-sm p-8">
                <div className="flex items-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mr-4">
                    <Plus className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Add WordPress Site</h1>
                    <p className="text-sm text-gray-600">Connect a new WordPress site to your dashboard</p>
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Globe className="w-4 h-4 mr-2" />
                      Site Name
                    </label>
                    <Input
                      type="text"
                      name="siteName"
                      value={formData?.siteName}
                      onChange={handleChange}
                      placeholder="My Awesome Blog"
                      required
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Globe className="w-4 h-4 mr-2" />
                      Site URL
                    </label>
                    <Input
                      type="url"
                      name="siteUrl"
                      value={formData?.siteUrl}
                      onChange={handleChange}
                      placeholder="https://example.com"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">Full URL including https://</p>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 mr-2" />
                      WordPress Username
                    </label>
                    <Input
                      type="text"
                      name="wpUsername"
                      value={formData?.wpUsername}
                      onChange={handleChange}
                      placeholder="admin"
                      required
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Lock className="w-4 h-4 mr-2" />
                      Application Password
                    </label>
                    <Input
                      type="password"
                      name="wpAppPassword"
                      value={formData?.wpAppPassword}
                      onChange={handleChange}
                      placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Generate from WordPress → Users → Profile → Application Passwords
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Default Category (Optional)
                    </label>
                    <Input
                      type="text"
                      name="defaultCategory"
                      value={formData?.defaultCategory}
                      onChange={handleChange}
                      placeholder="Uncategorized"
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? 'Saving...' : 'Save Site'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => navigate('/wp/sites')}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}